import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/Modal'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type SystemDTO, requestCenterApi } from '@/services/request-center-api'
import { useCrudState } from './useCrudState'

interface SystemForm {
	code: string
	name: string
	category: string
	ownerEmployeeId: string
}

const blankForm = (): SystemForm => ({ code: '', name: '', category: '', ownerEmployeeId: '' })

export function SystemsPage() {
	const crud = useCrudState<SystemDTO, SystemForm>()
	const { list, save, del } = useSystemsMutations(crud)
	return (
		<div className="space-y-4">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold tracking-tight">Systems</h1>
				<Button onClick={() => crud.openCreate(blankForm())}>New System</Button>
			</header>
			<SystemsTable
				list={list}
				deletePending={del.isPending}
				onEdit={(s) =>
					crud.openEdit(s, {
						code: s.code,
						name: s.name,
						category: s.category ?? '',
						ownerEmployeeId: s.ownerEmployeeId ?? '',
					})
				}
				onDelete={(id) => del.mutate(id)}
			/>
			<SystemFormModal
				open={crud.isOpen}
				editing={crud.isEditing}
				form={crud.form}
				saving={save.isPending}
				saveError={save.error}
				onChange={crud.setForm}
				onClose={crud.close}
				onSubmit={(input) => save.mutate(input)}
			/>
		</div>
	)
}

function useSystemsMutations(crud: ReturnType<typeof useCrudState<SystemDTO, SystemForm>>) {
	const queryClient = useQueryClient()
	const list = useQuery({ queryKey: ['systems'], queryFn: () => requestCenterApi.listSystems() })
	const save = useMutation({
		mutationFn: async (input: SystemForm) => {
			const payload = {
				code: input.code,
				name: input.name,
				category: input.category || null,
				ownerEmployeeId: input.ownerEmployeeId || null,
			}
			return crud.editing
				? requestCenterApi.updateSystem(crud.editing.id, payload as Partial<SystemDTO>)
				: requestCenterApi.createSystem(payload as Partial<SystemDTO>)
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['systems'] })
			crud.close()
		},
	})
	const del = useMutation({
		mutationFn: (id: string) => requestCenterApi.deleteSystem(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['systems'] }),
	})
	return { list, save, del }
}

function SystemsTable({
	list,
	deletePending,
	onEdit,
	onDelete,
}: {
	list: { data: SystemDTO[] | undefined; isLoading: boolean; error: unknown }
	deletePending: boolean
	onEdit: (s: SystemDTO) => void
	onDelete: (id: string) => void
}) {
	if (list.isLoading) return <TableSkeleton />
	if (list.error) return <ErrorState message="Failed to load systems" />
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Code</TableHead>
					<TableHead>Name</TableHead>
					<TableHead>Category</TableHead>
					<TableHead className="w-32 text-right">Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{(list.data ?? []).map((s) => (
					<TableRow key={s.id}>
						<TableCell className="font-mono">{s.code}</TableCell>
						<TableCell>{s.name}</TableCell>
						<TableCell className="text-muted-foreground">{s.category ?? '—'}</TableCell>
						<TableCell className="space-x-2 text-right">
							<Button variant="outline" size="sm" onClick={() => onEdit(s)}>
								Edit
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={deletePending}
								onClick={() => {
									if (window.confirm(`Delete ${s.code}?`)) onDelete(s.id)
								}}
							>
								Delete
							</Button>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function SystemFormModal({
	open,
	editing,
	form,
	saving,
	saveError,
	onChange,
	onClose,
	onSubmit,
}: {
	open: boolean
	editing: boolean
	form: SystemForm | null
	saving: boolean
	saveError: unknown
	onChange: (next: SystemForm) => void
	onClose: () => void
	onSubmit: (input: SystemForm) => void
}) {
	if (!form) return null
	return (
		<Modal open={open} onClose={onClose} title={editing ? 'Edit system' : 'New system'} maxWidth="md">
			<form
				onSubmit={(e) => {
					e.preventDefault()
					onSubmit(form)
				}}
				className="space-y-3"
			>
				<TextRow label="Code" value={form.code} onChange={(v) => onChange({ ...form, code: v })} required />
				<TextRow label="Name" value={form.name} onChange={(v) => onChange({ ...form, name: v })} required />
				<TextRow label="Category" value={form.category} onChange={(v) => onChange({ ...form, category: v })} />
				<TextRow
					label="Owner Employee ID"
					value={form.ownerEmployeeId}
					onChange={(v) => onChange({ ...form, ownerEmployeeId: v })}
				/>
				{saveError ? (
					<p role="alert" className="text-sm text-destructive">
						{messageFor(saveError)}
					</p>
				) : null}
				<div className="flex justify-end gap-2 pt-2">
					<Button type="button" variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" disabled={saving}>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</div>
			</form>
		</Modal>
	)
}

function TextRow({
	label,
	value,
	onChange,
	required,
}: {
	label: string
	value: string
	onChange: (next: string) => void
	required?: boolean
}) {
	return (
		<label className="block space-y-1">
			<span className="text-sm font-medium">
				{label}
				{required ? <span className="ml-0.5 text-destructive">*</span> : null}
			</span>
			<Input value={value} onChange={(e) => onChange(e.target.value)} required={required} />
		</label>
	)
}

function TableSkeleton() {
	return (
		<div className="space-y-2">
			{[0, 1, 2].map((i) => (
				<div key={i} className="h-10 animate-pulse rounded bg-muted/40" />
			))}
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}

function messageFor(err: unknown): string {
	return err instanceof Error ? err.message : 'Save failed'
}
